import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ClickOutSideDirective } from '../../../../ui-components/directives/clickOutSide.directive';
import { FEATURE_FLAGS } from '../../constants/feature-flags.constants';
import { RoleTypes } from '../../constants/role-types.constant';
import { isVendorPortalUrl } from '../../helpers/portal/is-vendor-portal-url';
import { Language } from '../../models/language.model';
import { Workspace } from '../../models/workspace.model';
import { AuthService } from '../../services/auth/auth.service';
import { FeatureFlagsService } from '../../services/feature-flags/feature-flags.service';
import { RoleHelperService } from '../../services/role-helper/role-helper.service';
import { ProfileDropdownButtonComponent } from './profile-dropdown-button/profile-dropdown-button.component';
import { ProfileDropdownMenuComponent } from './profile-dropdown-menu/profile-dropdown-menu.component';
import {
  LanguageSubMenuItems,
  PORTAL_SUB_MENU_ITEMS,
  PROFILE_DROPDOWN_MENU_ITEMS,
  ProfileDropdownItemSlugs,
} from './profile-dropdown.constants';
import {
  ProfileDropdownItem,
  ProfileDropdownSubItem,
} from './profile-dropdown.model';
import { ProfileDropdownSubMenuComponent } from './profile-dropdown-sub-menu/profile-dropdown-sub-menu.component';
import { ProfileActionsService } from './services/profile-actions.service';
import { ProfileDropdownService } from './services/profile-dropdown.service';

@Component({
  selector: 'lib-profile-dropdown',
  imports: [
    ProfileDropdownMenuComponent,
    ProfileDropdownButtonComponent,
    ProfileDropdownSubMenuComponent,
    ClickOutSideDirective,
  ],
  templateUrl: './profile-dropdown.component.html',
  styleUrl: './profile-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ProfileDropdownComponent implements OnInit {
  readonly ProfileDropdownItemSlugs = ProfileDropdownItemSlugs;

  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly profileDropdownService = inject(ProfileDropdownService);
  private readonly profileActions = inject(ProfileActionsService);
  private readonly roleHelper = inject(RoleHelperService);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  lang = signal<Language>(this.translocoService.getActiveLang() as Language);
  workspaceSubText = computed<string>(() => {
    const selectedWorkspace = this.authService.selectedWorkspace();
    return selectedWorkspace
      ? `${selectedWorkspace.name ?? selectedWorkspace.slug} . ${selectedWorkspace.business_line?.name['en'] ?? selectedWorkspace.business_line?.slug}`
      : '';
  });
  currentPortalLabel = computed<string>(() =>
    this.translocoService.translate(
      this.isOnVendorPortal()
        ? 'profile_dropdown_menu_vendor_portal_label'
        : 'profile_dropdown_menu_employee_portal_label'
    )
  );
  // Reactive so the workspace switcher appears once the tenant list loads.
  profileMenuItems = computed<ProfileDropdownItem[]>(() => {
    const hasMultipleWorkspaces = this.authService.workspaces().length > 1;
    const showPortalSettings = this.canShowPortalSettings();

    return PROFILE_DROPDOWN_MENU_ITEMS.filter(item => {
      if (item.slug === ProfileDropdownItemSlugs.WORKSPACE) {
        return hasMultipleWorkspaces;
      }
      if (item.slug === ProfileDropdownItemSlugs.PORTAL) {
        return showPortalSettings;
      }
      return true;
    }).map(item => {
      if (item.slug === ProfileDropdownItemSlugs.WORKSPACE) {
        return { ...item, subText: this.workspaceSubText() };
      }
      if (item.slug === ProfileDropdownItemSlugs.PORTAL) {
        return { ...item, subText: this.currentPortalLabel() };
      }
      return item;
    });
  });
  isMainMode = signal<boolean>(true);
  selectedMenuItem = signal<ProfileDropdownItem | null>(null);
  subMenuItems = signal<ProfileDropdownSubItem[]>([]);
  selectedSubMenuItem = signal<ProfileDropdownSubItem | null>(null);
  isOpen = computed(() => this.profileDropdownService.isOpen());

  ngOnInit(): void {
    // Tenants aren't persisted, so load them once to drive the switcher's visibility.
    this.handleWorkspaces();
  }

  private canShowPortalSettings(): boolean {
    const isPortalSettingsEnabled =
      this.featureFlagsService.getFeatureFlagValue(
        FEATURE_FLAGS.SYSTEM.portal_settings
      );
    if (!isPortalSettingsEnabled) {
      return false;
    }
    const roles = this.authService.selectedWorkspace()?.user_roles ?? [];
    return (
      roles.includes(RoleTypes.EMPLOYEE) && roles.includes(RoleTypes.VENDOR)
    );
  }

  toggleDropdown() {
    this.profileDropdownService.isOpen.set(
      !this.profileDropdownService.isOpen()
    );
  }

  closeDropdown() {
    this.profileDropdownService.isOpen.set(false);
  }

  selectMenuItem(item: ProfileDropdownItem) {
    this.selectedMenuItem.set(item);
    switch (item.slug) {
      case ProfileDropdownItemSlugs.WORKSPACE:
        this.isMainMode.set(false);
        this.handleWorkspaces();
        break;

      case ProfileDropdownItemSlugs.CREATE_WORKSPACE:
        this.createWorkspace();
        break;

      case ProfileDropdownItemSlugs.LANGUAGE:
        this.isMainMode.set(false);
        this.setLanguageSubMenuItems();
        break;

      case ProfileDropdownItemSlugs.PORTAL:
        this.isMainMode.set(false);
        this.setPortalSubMenuItems();
        break;

      case ProfileDropdownItemSlugs.LOGOUT:
        this.logout();
        break;
    }
  }

  selectSubMenuItem(subItem: ProfileDropdownSubItem) {
    this.selectedSubMenuItem.set(subItem);
    this.toggleDropdown();

    if (this.selectedMenuItem()?.slug === ProfileDropdownItemSlugs.LANGUAGE) {
      this.switchLanguage(subItem.slug as Language);
    }

    if (this.selectedMenuItem()?.slug === ProfileDropdownItemSlugs.WORKSPACE) {
      this.switchWorkspace(subItem.slug);
    }

    if (this.selectedMenuItem()?.slug === ProfileDropdownItemSlugs.PORTAL) {
      this.switchPortal(subItem.slug);
    }
  }

  switchLanguage(lang: Language): void {
    this.setLanguageSubMenuItems();
    this.profileActions.changeLanguage(lang);
  }

  // for setting language sub menu items
  setLanguageSubMenuItems(): void {
    const currentLanguageSubMenuItems = LanguageSubMenuItems.map(subItem => ({
      ...subItem,
      isSelected: subItem.slug === this.translocoService.getActiveLang(),
    }));
    this.subMenuItems.set(currentLanguageSubMenuItems);
  }

  handleWorkspaces(): void {
    // Always refetch on open — tenants aren't persisted, and the list may have
    // changed. Keep the current selectedWorkspace so its row stays highlighted.
    this.profileActions
      .loadWorkspaces()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(workspaces => this.setWorkspaceSubMenuItems(workspaces));
  }

  setWorkspaceSubMenuItems(workspaces: Workspace[]): void {
    const workspaceSubMenuItems = workspaces.map(workspace => ({
      slug: workspace.slug,
      mainText: workspace.name || workspace.slug,
      img: workspace.images?.logo,
      isSelected: workspace.slug === this.authService.selectedWorkspace()?.slug,
      subText:
        workspace.business_line?.name?.[this.lang()] ||
        workspace.business_line?.slug,
      isMainTextTranslatable: false,
      showImage: true,
    }));
    this.subMenuItems.set(workspaceSubMenuItems);
  }

  setPortalSubMenuItems(): void {
    const isVendorPortal = this.isOnVendorPortal();
    this.subMenuItems.set(
      PORTAL_SUB_MENU_ITEMS.map(subItem => ({
        ...subItem,
        isSelected:
          subItem.slug === RoleTypes.VENDOR ? isVendorPortal : !isVendorPortal,
      }))
    );
  }

  private isOnVendorPortal(): boolean {
    return isVendorPortalUrl(this.router.url);
  }

  switchPortal(roleSlug: string): void {
    const workspace = this.authService.selectedWorkspace();
    if (
      !workspace ||
      (roleSlug !== RoleTypes.EMPLOYEE && roleSlug !== RoleTypes.VENDOR)
    ) {
      return;
    }
    this.roleHelper.handleNavigationBasedOnRole([roleSlug], '', '', workspace);
    this.isMainMode.set(true);
  }

  switchWorkspace(workspaceSlug: string): void {
    this.profileActions.switchWorkspace(workspaceSlug);
  }

  createWorkspace(): void {
    this.closeDropdown();
    this.profileActions.createWorkspace();
  }

  logout(): void {
    this.profileActions.logout();
  }
}
